import { Injectable } from "@angular/core";
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
    HttpErrorResponse,
} from "@angular/common/http";
import { Observable, BehaviorSubject, of, throwError } from "rxjs";
import { AuthService } from "src/app/services";
import {
    catchError,
    switchMap,
    filter,
    take,
    finalize,
    map,
} from "rxjs/operators";
import { Tokens } from "../models/tokens.model";
import { Router } from "@angular/router";
import { ToastService } from "../services/toast.service";
import { TranslateService } from "@ngx-translate/core";
import { RoutePaths } from "../shared/constants/routePaths.constants";
import { PUBLIC_ALERT_RESPONSE_ENDPOINTS } from "../shared/constants/alertResponse.constants";
import { TOKEN_ENDPOINTS } from "../shared/constants/authEndpoints.constants";
import * as Sentry from "@sentry/angular";

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
    private readonly UNAUTHORIZED = "Unauthorized";
    private isRefreshing = false;
    private refreshTokenSubject: BehaviorSubject<any> =
        new BehaviorSubject<any>(null);

    constructor(
        private authService: AuthService,
        private router: Router,
        private toastService: ToastService,
        private translateService: TranslateService,
    ) {}

    intercept(
        request: HttpRequest<any>,
        next: HttpHandler,
    ): Observable<HttpEvent<any>> {
        request = request.clone({
            setHeaders: {
                "Accept-Language": this.translateService.currentLang || "en",
            },
        });

        if (this.authService.isLoggedIn() && !this.isPublicAlertResponseRequest(request)) {
            request = this.addToken(request, this.authService.getAccessToken());
        }

        return next.handle(request).pipe(
            catchError((error) => {
                if (
                    error instanceof HttpErrorResponse &&
                    error.status === 401 &&
                    !this.isPublicAlertResponseRequest(request) &&
                    !this.isTokenEndpointRequest(request) &&
                    (!(error.error instanceof Array) ||
                        error.error.length < 1 ||
                        error.error[0].code !== "login_failure")
                ) {
                    return this.handle401Error(request, next);
                } else {
                    return throwError(error);
                }
            }),
        );
    }

    /**
     * A signed single-use link authenticates its own recipient via the token
     * in the URL, not a logged-in session, so an expired/used token 401s
     * without ever meaning "log this browser out" - that 401 belongs to this
     * page's own "link is no longer valid" state, not the global redirect.
     */
    private isPublicAlertResponseRequest(request: HttpRequest<any>): boolean {
        return PUBLIC_ALERT_RESPONSE_ENDPOINTS.some((endpoint) =>
            request.url.includes(endpoint),
        );
    }

    /**
     * The refresh and revoke calls go through this interceptor too. A 401 on
     * one of them must fail that call, not queue it behind the refresh it is
     * part of: the refresh would wait on itself, never finish, keep the
     * cross-tab refresh lock held, and freeze every tab.
     */
    private isTokenEndpointRequest(request: HttpRequest<any>): boolean {
        return TOKEN_ENDPOINTS.some((endpoint) => request.url.includes(endpoint));
    }

    private addToken(request: HttpRequest<any>, token: string) {
        // Don't add auth bearer token to amazon requests
        if (request.url.includes("amazon")) {
            return request;
        }
        const update = { setHeaders: { Authorization: `Bearer ${token}` } };
        if (request.url.includes("Auth/Logout")) {
            update["body"] = {
                refreshToken: this.authService.getRefreshToken(),
            };
        } else if (request.url.includes("Auth/ImpersonateUser")) {
            update["body"] = {
                refreshToken: this.authService.getRefreshToken(),
                userId: request.body.userId,
            };
        }
        return request.clone(update);
    }

    private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
        if (!this.isRefreshing) {
            this.startRefresh();
        }

        return this.refreshTokenSubject.pipe(
            filter((token) => token != null),
            take(1),
            switchMap((accessToken) => {
                if (accessToken === this.UNAUTHORIZED) {
                    return throwError(() => accessToken);
                }

                return next.handle(this.addToken(request, accessToken));
            }),
        );
    }

    /**
     * The refresh runs in its own subscription rather than inside the request
     * that hit the 401: callers like the alert bell cancel their request on
     * every poll, and a cancelled refresh would otherwise never reset
     * isRefreshing or publish its result, leaving every later 401 in the tab
     * waiting forever with no logout.
     */
    private startRefresh(): void {
        this.isRefreshing = true;
        this.refreshTokenSubject.next(null);
        const staleAccessToken = this.authService.getAccessToken();

        this.authService
            .refreshToken()
            .pipe(
                switchMap((tokens: Tokens | null) =>
                    this.resolveRefreshResult(tokens, staleAccessToken),
                ),
                finalize(() => {
                    if (!this.authService.isLoggedIn()) {
                        this.redirectToLogin();
                    }
                }),
            )
            .subscribe({
                next: (result) => this.publishRefreshResult(result),
                error: () => this.publishRefreshResult(this.UNAUTHORIZED),
            });
    }

    private publishRefreshResult(result: string): void {
        this.isRefreshing = false;
        this.refreshTokenSubject.next(result);
    }

    private resolveRefreshResult(
        tokens: Tokens | null,
        staleAccessToken: string,
    ): Observable<string> {
        if (tokens !== null) {
            return of(tokens.accessToken);
        }

        const currentAccessToken = this.authService.getAccessToken();
        if (currentAccessToken && currentAccessToken !== staleAccessToken) {
            Sentry.addBreadcrumb({
                category: "auth",
                message: "Another tab rotated tokens; adopting them",
                level: "info",
            });
            return of(currentAccessToken);
        }

        Sentry.addBreadcrumb({
            category: "auth",
            message: "Refresh token failed; revoking tokens and logging out",
            level: "warning",
        });
        Sentry.captureMessage(
            "Refresh-token failure: revoking session and logging out",
            "warning",
        );

        return this.authService
            .revokeTokens(staleAccessToken)
            .pipe(map(() => this.UNAUTHORIZED));
    }

    private redirectToLogin(): void {
        this.router.navigate([RoutePaths.login]);
        this.toastService.showSuccess(
            this.translateService.instant('Logged out'),
            true,
        );
        const cdkOverlay = document.querySelector('.cdk-overlay-container');
        if (cdkOverlay) {
            cdkOverlay.innerHTML = '';
        }
    }
}
